import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Box, Typography, Button, Container, TypographyProps, IconButton, Paper, Modal } from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import useScrollToTop from '../../hooks/useScrollToTop';

const StyledTypography = styled(Typography)<TypographyProps>(({ theme }) => ({
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
}));

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '300px',
  height: 'auto',
  borderRadius: '8px',
  objectFit: 'cover',
}));

const CarouselContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const CarouselCaption = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  padding: theme.spacing(2),
  background: 'rgba(0, 0, 0, 0.6)',
  color: 'white',
  borderBottomLeftRadius: '8px',
  borderBottomRightRadius: '8px',
}));

const CarouselImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '400px',
  objectFit: 'cover',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

const EnlargedImage = styled('img')({
  maxWidth: '90vw',
  maxHeight: '90vh',
  objectFit: 'contain',
});

const ModalContent = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'white',
  padding: theme.spacing(2),
  borderRadius: '8px',
  outline: 'none',
}));

const images = [
  {
    src: '/images/jingning-8f32a6b4.jpg',
    descriptionKey: 'team.jingningLu.carousel.euTeam'
  },
  {
    src: '/images/jingning-9c74d1f2.jpg',
    descriptionKey: 'team.jingningLu.carousel.latinAmericaTeam'
  },
  {
    src: '/images/jingning-7e93ab55.jpg',
    descriptionKey: 'team.jingningLu.carousel.chinaTeam'
  },
  {
    src: '/images/jingning-2bd5ca11.jpg',
    descriptionKey: ''
  },
  {
    src: '/images/jingning-2bd5ca10a.jpg',
    descriptionKey: 'team.jingningLu.carousel.hatoyama1'
  },
  {
    src: '/images/jingning-2bd5ca10b.jpg',
    descriptionKey: 'team.jingningLu.carousel.hatoyama2'
  },
  {
    src: '/images/jingning-1f2ea783.jpg',
    descriptionKey: 'team.jingningLu.carousel.cambridge'
  },
  {
    src: '/images/jingning-4aa8716c.jpg',
    descriptionKey: 'team.jingningLu.carousel.cambridgeVisit'
  },
  {
    src: '/images/jingning_mischa.png',
    descriptionKey: 'team.jingningLu.carousel.mischa'
  },
  {
    src: '/images/jingning_argentina_embassador_santiago.png',
    descriptionKey: 'team.jingningLu.carousel.santiago'
  },
  {
    src: '/images/jingning-5d4312ef.jpg',
    descriptionKey: 'team.jingningLu.carousel.royalAcademy'
  },
  {
    src: '/images/jingning-0ce49a7d.jpg',
    descriptionKey: 'team.jingningLu.carousel.stanford'
  },
  {
    src: '/images/jingning_nus.png',
    descriptionKey: 'team.jingningLu.carousel.nus'
  }
];

const JingningLu: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
  const [isImageEnlarged, setIsImageEnlarged] = React.useState(false);
  useScrollToTop();

  const handlePrevious = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleImageClick = () => {
    setIsImageEnlarged(true);
  };

  const handleCloseEnlarged = () => {
    setIsImageEnlarged(false);
  };

  // Add keyboard event handler
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isImageEnlarged) return;
      
      switch (event.key) {
        case 'ArrowLeft':
          handlePrevious();
          break;
        case 'ArrowRight':
          handleNext();
          break;
        case 'Escape':
          handleCloseEnlarged();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isImageEnlarged]);

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'grey.50',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* Hero Section */}
      <Box sx={{ 
        bgcolor: 'white',
        width: '100%',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <Container maxWidth="lg" sx={{ py: 8, px: { xs: 4, sm: 6, lg: 8 } }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <StyledTypography variant="h2" component="h1" gutterBottom>
                {t('team.jingningLu.name')}
              </StyledTypography>
              <StyledTypography variant="h5" color="text.secondary" sx={{ mt: 3 }}>
                {t('team.jingningLu.title')}
              </StyledTypography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Bio Section */}
      <Container maxWidth="lg" sx={{ py: 8, px: { xs: 4, sm: 6, lg: 8 } }}>
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
            gap: 4,
            alignItems: 'start'
          }}>
            <Box>
              <StyledImage 
                src="/images/卢竞宁照片.jpg" 
                alt={t('team.jingningLu.name')}
              />
            </Box>
            <Box>
              <StyledTypography variant="body1" color="text.secondary" paragraph>
                {t('team.jingningLu.bio.paragraph1')}
              </StyledTypography>
              <StyledTypography variant="body1" color="text.secondary" paragraph>
                {t('team.jingningLu.bio.paragraph2')}
              </StyledTypography>
              {/* <StyledTypography variant="body1" color="text.secondary" paragraph>
                {t('team.jingningLu.bio.paragraph3')}
              </StyledTypography> */}
              <CarouselContainer>
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  style={{ width: '100%' }}
                >
                  <Paper elevation={3}>
                    <CarouselImage
                      src={images[currentImageIndex].src}
                      alt={images[currentImageIndex].descriptionKey ? t(images[currentImageIndex].descriptionKey) : ''}
                      onClick={handleImageClick}
                    />
                    {images[currentImageIndex].descriptionKey && (
                      <CarouselCaption>
                        <Typography variant="body1">
                          {t(images[currentImageIndex].descriptionKey)}
                        </Typography>
                      </CarouselCaption>
                    )}
                  </Paper>
                </motion.div>
                
                <IconButton
                  onClick={handlePrevious}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
                
                <IconButton
                  onClick={handleNext}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </CarouselContainer>

              <Modal
                open={isImageEnlarged}
                onClose={handleCloseEnlarged}
                aria-labelledby="enlarged-image-modal"
              >
                <ModalContent>
                  <IconButton
                    onClick={handleCloseEnlarged}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  
                  <IconButton
                    onClick={handlePrevious}
                    sx={{
                      position: 'absolute',
                      left: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                    }}
                  >
                    <ArrowBackIosNewIcon />
                  </IconButton>

                  <IconButton
                    onClick={handleNext}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'white',
                      bgcolor: 'rgba(0, 0, 0, 0.5)',
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' }
                    }}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>

                  <EnlargedImage
                    src={images[currentImageIndex].src}
                    alt={images[currentImageIndex].descriptionKey ? t(images[currentImageIndex].descriptionKey) : ''}
                  />
                  
                  {images[currentImageIndex].descriptionKey && (
                    <Box sx={{ 
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: 2,
                      background: 'rgba(0, 0, 0, 0.6)',
                      color: 'white',
                      textAlign: 'center'
                    }}>
                      <Typography variant="body1">
                        {t(images[currentImageIndex].descriptionKey)}
                      </Typography>
                    </Box>
                  )}
                </ModalContent>
              </Modal>

              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  component={Link}
                  to="/about"
                  variant="contained"
                  color="primary"
                >
                  {t('team.jingningLu.backButton')}
                </Button>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default JingningLu; 